// Calamity definitions. A civilization's story is not only its wars: nature,
// its own hands, its markets and its people can all turn on it. Each calamity
// is a one-shot strike with a themed effect; systems/calamities.ts rolls the
// clock and applies them. Names and descriptions are English fallbacks; the
// content i18n tables translate them (`calamity.<id>.name` / `.desc`).

import type { ResourceId } from './resources';

export type CalamityCategory = 'natural' | 'manmade' | 'economic' | 'social';

export interface CalamityDef {
  id: string;
  icon: string;
  category: CalamityCategory;
  name: string; // EN fallback
  desc: string; // EN fallback, pure flavor (numbers show as floating losses)
  /** Fractions of current stock lost, per resource. */
  loot?: Partial<Record<ResourceId, number>>;
  /** Fraction of current population lost (floored at BALANCE.calamities.popFloor). */
  popFraction?: number;
  /** Fraction of current spoils lost. */
  spoilsFraction?: number;
  /** Temporary production debuff. */
  debuff?: { mult: number; secs: number };
  /** Number of random alive resource nodes knocked out (respawn as usual). */
  depleteNodes?: number;
}

export const CALAMITIES: CalamityDef[] = [
  // ---- Natural ----
  {
    id: 'quake', icon: '🌋', category: 'natural',
    name: 'Earthquake',
    desc: 'The ground split beneath the settlement. Stores collapsed into the cracks.',
    loot: { stone: 0.25, ore: 0.2 },
    depleteNodes: 2,
  },
  {
    id: 'flood', icon: '🌊', category: 'natural',
    name: 'Great Flood',
    desc: 'The river rose in the night and took what it wanted.',
    loot: { wood: 0.25, stone: 0.15 },
    depleteNodes: 2,
  },
  {
    id: 'drought', icon: '☀️', category: 'natural',
    name: 'Drought',
    desc: 'The wells run low. Everything moves slower under the flat white sky.',
    debuff: { mult: 0.55, secs: 60 },
  },
  {
    id: 'plague', icon: '🦠', category: 'natural',
    name: 'Plague',
    desc: 'It arrived with a caravan and stayed. The healers did what they could.',
    popFraction: 0.25,
  },
  // ---- Man-made ----
  {
    id: 'great-fire', icon: '🔥', category: 'manmade',
    name: 'Great Fire',
    desc: 'It started in a granary. By dawn a whole quarter was ash.',
    loot: { wood: 0.35 },
    popFraction: 0.08,
  },
  {
    id: 'collapse', icon: '🏚️', category: 'manmade',
    name: 'The Collapse',
    desc: 'The grand works project failed. The engineers blame each other.',
    loot: { stone: 0.2, ore: 0.25 },
  },
  // ---- Economic ----
  {
    id: 'market-crash', icon: '📉', category: 'economic',
    name: 'Market Crash',
    desc: 'The spoils market folded overnight. Paper fortunes, paper ashes.',
    spoilsFraction: 0.35,
  },
  {
    id: 'embargo', icon: '⛔', category: 'economic',
    name: 'Trade Embargo',
    desc: 'The routes are closed. The caravans wait at borders that used to be lines on a map.',
    debuff: { mult: 0.6, secs: 50 },
  },
  // ---- Social ----
  {
    id: 'unrest', icon: '✊', category: 'social',
    name: 'Civil Unrest',
    desc: 'The crowd in the square stopped chanting and started taking.',
    popFraction: 0.15,
    spoilsFraction: 0.15,
  },
  {
    id: 'exodus', icon: '🏜️', category: 'social',
    name: 'The Exodus',
    desc: 'A caravan left before sunrise. They took their tents, their herds and their doubts.',
    popFraction: 0.3,
  },
];

export const CALAMITY_BY_ID: Record<string, CalamityDef> = Object.fromEntries(
  CALAMITIES.map((c) => [c.id, c]),
);
